import { ReactNode, useRef } from "react";
import { useScroll, useTransform } from "motion/react";
import { useTheme } from "@hooks/useTheme";
import { usePaletteValues } from "@hooks/usePaletteValues";

// Scroll-triggered wrapper that transitions the page background from
// `theme.background` back to `palette.deep` as this container enters the
// viewport — creating a bookend effect with the hero.
export const BackgroundDeepContainer = ({
  children,
}: {
  children: ReactNode;
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const { theme, backgroundColor } = useTheme();
  const paletteValues = usePaletteValues(theme.palette);

  // progress goes 0 → 1 as the container's top edge moves from the bottom
  // of the viewport to the top of the viewport.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "start start"],
  });

  const transition = useTransform(
    scrollYProgress,
    [0, 1],
    [paletteValues[theme.background](), paletteValues.deep()]
  );

  // progress <= 0 means the container is below the viewport — don't touch
  // backgroundColor so the Hero retains control on initial load.
  useTransform(() => {
    const progress = scrollYProgress.get();
    if (progress <= 0) return;
    backgroundColor.set(transition.get());
  });

  return <div ref={sectionRef}>{children}</div>;
};
